// Печатная форма и выгрузки протокола (016: contracts/api.md, «PrintOptions»; research Р4, Р6, Р7).
// Форма строится сервером из подтверждённой ревизии рабочей недели или из тела снимка, а не из
// экрана (PRINT-01, WEEK-06). Бумага — только A4 (конституция; уточнение 24.10.2026). Скрытые
// в Excel строки по умолчанию не печатаются (уточнение 24.10.2026); «скрыто в протоколе» в печать
// и выгрузку не идёт (словарь). Состояние работы печатается всегда — подписью у каждого пункта:
// статус читается при чёрно-белой печати (конституция, «Статусы… в печати»; анализ 016, C1).
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';

export const PRINT_FONT_MIN = 9;
export const PRINT_FONT_MAX = 12;
export const PRINT_FONT_DEFAULT = 10;
/** Число неотправленных правок клиента для предупреждения в форме (FR-020). */
export const PRINT_UNSENT_MAX = 10_000;

/** Флаг в строке запроса: «1»/«true» — да, «0»/«false» или нет — нет. */
const FlagSchema = z
  .union([z.boolean(), z.enum(['1', '0', 'true', 'false'])])
  .transform((value) => value === true || value === '1' || value === 'true')
  .default(false);

export const progressModes = ['since_snapshot', 'last3', 'all', 'none'] as const;
export const ProgressModeSchema = z.enum(progressModes);
export type ProgressMode = z.infer<typeof ProgressModeSchema>;
export const progressModeLabels: Record<ProgressMode, string> = {
  since_snapshot: 'С прошлой планёрки',
  last3: 'Последние 3',
  all: 'Весь',
  none: 'Не печатать',
};

export const PrintOptionsSchema = z
  .strictObject({
    snapshot: IdSchema.optional(),
    scope: z.enum(['all', 'branch']).default('all'),
    /** Корень ветви при scope = branch. */
    branch: IdSchema.optional(),
    hidden: FlagSchema,
    orientation: z.enum(['landscape', 'portrait']).default('landscape'),
    font: z.coerce
      .number()
      .int()
      .min(PRINT_FONT_MIN)
      .max(PRINT_FONT_MAX)
      .default(PRINT_FONT_DEFAULT),
    start: FlagSchema,
    marks: FlagSchema,
    progressMode: ProgressModeSchema.default('since_snapshot'),
    signatures: FlagSchema,
    unsent: z.coerce.number().int().min(0).max(PRINT_UNSENT_MAX).default(0),
  })
  .refine((options) => options.scope !== 'branch' || options.branch !== undefined, {
    message: 'Выберите ветвь для печати',
    path: ['branch'],
  });
export type PrintOptions = z.infer<typeof PrintOptionsSchema>;
export type PrintOptionsInput = z.input<typeof PrintOptionsSchema>;

export const ExportXlsxQuerySchema = z.strictObject({
  snapshot: IdSchema.optional(),
  progressMode: ProgressModeSchema.default('since_snapshot'),
});

/** Версия формата JSON-копии протокола (research Р7): копия данных, не резервная копия системы. */
export const PROTOCOL_JSON_FORMAT = 'kp-protocol/1';
